'use client';

import * as React from 'react';
import { Badge, IconButton, Tooltip } from '@mui/material';
import { Notifications as NotificationsIcon, ErrorOutline as ErrorOutlineIcon } from '@mui/icons-material';
import NotificationsModal from './NotificationsModal';
import { api } from '@/lib/api';

interface NotificationItem {
  id: string | number;
  title?: string;
  message: string;
  read: boolean;
  type?: 'info' | 'warning' | 'success' | 'error' | string;
  createdAt?: string;
  userId?: string;
  link?: string;
  metadata?: Record<string, unknown>;
}

interface NotificationResponse {
  total?: number;
  items?: NotificationItem[];
  data?: NotificationItem[];
}

interface NotificationBadgeProps {
  userId: string;
}

// Extiende el tipo de api.notifications con un método opcional (si existe en runtime)
type NotificationsApiWithCount = typeof api.notifications & {
  getUnreadCount?: () => Promise<number>;
};

export default function NotificationBadge({ userId }: NotificationBadgeProps) {
  const [open, setOpen] = React.useState(false);
  const [unreadCount, setUnreadCount] = React.useState<number | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const fetchUnreadCount = React.useCallback(async () => {
    try {
      const notificationsApi = api.notifications as NotificationsApiWithCount;

      // Preferimos el endpoint ligero si existe
      if (typeof notificationsApi.getUnreadCount === 'function') {
        const count = await notificationsApi.getUnreadCount();
        setUnreadCount(count);
        setError(null);
        return;
      }

      // Fallback: derivar del summary
      const response = (await api.notifications.getSummary()) as NotificationResponse | NotificationItem[];
      const items = (Array.isArray(response)
        ? response
        : 'items' in response && Array.isArray(response.items)
          ? response.items
          : 'data' in response && Array.isArray(response.data)
            ? response.data
            : []) as NotificationItem[];

      const count = items.filter((n) => !n.read).length;
      setUnreadCount(count);
      setError(null);
    } catch (err: unknown) {
      console.error('NotificationBadge.fetchUnreadCount failed:', err);
      setError('Failed to load notifications');
      setUnreadCount(0);
    }
  }, []);

  React.useEffect(() => {
    fetchUnreadCount();
  }, [open, fetchUnreadCount]);

  return (
    <>
      <Tooltip title={error || 'Notifications'}>
        <span>
          <IconButton
            size="large"
            aria-label={error || `show ${unreadCount ?? 0} new notifications`}
            color="inherit"
            onClick={() => setOpen(true)}
            disabled={error !== null}
          >
            <Badge
              badgeContent={unreadCount ?? 0}
              color={error ? 'default' : 'error'}
              overlap="circular"
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              sx={{
                '& .MuiBadge-badge': {
                  backgroundColor: error ? 'default' : '#f44336',
                  color: 'white',
                  fontSize: '0.6rem',
                  height: 16,
                  minWidth: 16,
                  padding: '0 4px',
                },
              }}
            >
              {error ? <ErrorOutlineIcon color="error" /> : <NotificationsIcon />}
            </Badge>
          </IconButton>
        </span>
      </Tooltip>

      <NotificationsModal open={open} onClose={() => setOpen(false)} userId={userId} />
    </>
  );
}
